import Vue from 'vue';
import {
  handlerResponse,
  setNotifyData
} from '@/helpers/commonHelpers';
import {
  formatNumber,
  alignForNumber,
  getNodeChildDetails,
  addGroupingInResponseData,
  LinkCellRenderer,
  autoResize
} from '@/helpers/renderHelpers';

export default {
  namespaced: true,
  state: {
    wood: [],
    measure_types: ['tonne', 'pieces'],
    measure_types_si: [
      {key: 'tonne', name: 't'},
      {key: 'pieces', name: 'pcs'}
    ],
    incoterms: [],
    users: [],
    delivery_addresses: [],
    delivery_methods: [],
    qualities_list: [],
    payment_terms: [],
    partners: [],
    countries: [],
    profiles: [],
    stock_addresses: [],
    periods: [],
    processes: [],
    production_places: [],
    invoice_additional_info: [],
    groups_with_hierarchy: [],
    groups: [],
    imprs: [],
    impr_synonyms: [],
    wood_certificates: [],
    packages_lengths: [],
    packages_thicknesses: [],
    packages_widths: [],
    pieces_list: [],
    humidity_list: [],
    manufacture_exports: [],
    packets_reports: [],
    process_prices: [],
    fixed_prices: [],
    packages_scores: [],
    shipment_driver_names: [],
    product_templates: [],
    templates: [],
    operations: [],
    movement_groups: [],
    general_documents: [],
    shortInvoiceTypesList: [],
    pallet_sizes: [],
    bag_logos: [],
    pellet_diameters: [],
    total_weights: [],
    unit_weights: [],
    salesOrders: [],
    text_notes: [],
    correction_reasons: [],
    base_currency: {}
  },
  getters: {
    wood(state) {
      return state.wood;
    },
    incoterms(state) {
      return state.incoterms;
    },
    delivery_methods(state) {
      return state.delivery_methods;
    },
    qualities_list(state) {
      return state.qualities_list;
    },
    users(state) {
      return state.users
    },
    delivery_addresses(state) {
      return state.delivery_addresses
    },
    measure_types(state) {
      return state.measure_types;
    },
    payment_terms(state) {
      return state.payment_terms;
    },
    partners(state) {
      return state.partners;
    },
    buyers(state) {
      return state.partners.filter(item => item.roles.includes('customer'));
    },
    sellers(state) {
      return state.partners.filter(item => item.roles.includes('seller'));
    },
    saleAgents(state) {
      return state.partners.filter(item => item.roles.includes('sale_agent'));
    },
    purchaseAgents(state) {
      return state.partners.filter(item => item.roles.includes('purchase_agent'));
    },
    carriers(state) {
      return state.partners.filter(item => item.roles.includes('carrier'));
    },
    suppliers(state) {
      return state.partners.filter(item => item.roles.includes('supplier'));
    },
    offshore_companies(state) {
      return state.partners.filter(item => item.roles.includes('own_company'));
    },
    countries(state) {
      return state.countries;
    },
    profiles(state) {
      return state.profiles;
    },
    stock_addresses(state) {
      return state.stock_addresses;
    },
    periods(state) {
      return state.periods
    },
    processes(state) {
      return state.processes;
    },
    production_places(state) {
      return state.production_places;
    },
    invoice_additional_info(state) {
      return state.invoice_additional_info;
    },
    groups_with_hierarchy(state) {
      return state.groups_with_hierarchy;
    },
    groups(state) {
      return state.groups;
    },
    imprs(state) {
      return state.imprs;
    },
    impr_synonyms(state) {
      return state.impr_synonyms;
    },
    wood_certificates(state) {
      return state.wood_certificates;
    },
    packages_lengths(state) {
      return state.packages_lengths;
    },
    packages_thicknesses(state) {
      return state.packages_thicknesses;
    },
    packages_widths(state) {
      return state.packages_widths;
    },
    pieces_list(state) {
      return state.pieces_list;
    },
    humidity_list(state) {
      return state.humidity_list;
    },
    manufacture_exports(state) {
      return state.manufacture_exports;
    },
    packets_reports(state) {
      return state.packets_reports;
    }
  },
  mutations: {
    setWood(state, payLoad) {
      state.wood = payLoad;
    },
    getIncoterms(state, payLoad) {
      state.incoterms = payLoad;
    },
    getDeliveryMethods(state, payLoad) {
      state.delivery_methods = payLoad;
    },
    getUsers(state, payLoad) {
      state.users = payLoad;
    },
    getDeliveryAddresses(state, payLoad) {
      state.delivery_addresses = payLoad;
    },
    setQualitiesList(state, payLoad) {
      state.qualities_list = payLoad;
    },
    getPaymentTerms(state, payLoad) {
      state.payment_terms = payLoad;
    },
    setPartners(state, payLoad) {
      state.partners = payLoad;
    },
    getCountries(state, payLoad) {
      state.countries = payLoad.countries;
    },
    setProfiles(state, payLoad) {
      state.profiles = payLoad;
    },
    getStockAddresses(state, payLoad) {
      state.stock_addresses = payLoad;
    },
    getPeriods(state, payLoad) {
      state.periods = payLoad;
    },
    getProcesses(state, payLoad) {
      state.processes = payLoad;
    },
    getProductionPlaces(state, payLoad) {
      state.production_places = payLoad;
    },
    getInvoiceAdditionalInfo(state, payLoad) {
      state.invoice_additional_info = payLoad;
    },
    setGroupsWithHierarchy(state, payLoad) {
      state.groups_with_hierarchy = payLoad;
    },
    setGroups(state, payLoad) {
      state.groups = payLoad.map(group => {
        delete group.children;
        return group;
      });
    },
    setImprs(state, payLoad) {
      state.imprs = payLoad;
    },
    setImprSynonyms(state, payLoad) {
      state.impr_synonyms = payLoad;
    },
    setWoodCertificates(state, payLoad) {
      state.wood_certificates = payLoad;
    },
    setPackagesLengths(state, payLoad) {
      state.packages_lengths = payLoad;
    },
    setPackagesThicknesses(state, payLoad) {
      state.packages_thicknesses = payLoad;
    },
    setPackagesWidths(state, payLoad) {
      state.packages_widths = payLoad;
    },
    setPiecesList(state, payLoad) {
      state.pieces_list = payLoad;
    },
    setHumidityList(state, payLoad) {
      state.humidity_list = payLoad;
    },
    setManufactureExports(state, payLoad) {
      state.manufacture_exports = payLoad;
    },
    setPacketsReports(state, payLoad) {
      state.packets_reports = payLoad;
    },
    setProcessPrices(state, payLoad) {
      state.process_prices = payLoad;
    },
    setFixedPrices(state, payLoad) {
      state.fixed_prices = payLoad;
    },
    setPackagesScores(state, payload) {
      state.packages_scores = payload;
    },
    setShipmentDriverNames(state, payLoad) {
      state.shipment_driver_names = payLoad;
    },
    setProductTemplates(state, payLoad) {
      state.product_templates = payLoad;
    },
    setMovementGroups(state, data) {
      state.movement_groups = data;
    },
    setTemplates(state, data) {
      state.templates = data;
    },
    setOperations(state, data) {
      state.operations = data;
    },
    setGeneralDocuments(state, data) {
      state.general_documents = data;
    },
    setShortInvoiceTypesList(state, data) {
      state.shortInvoiceTypesList = data;
    },
    setPalletSizes(state, data) {
      state.pallet_sizes = data;
    },
    setBagLogos(state, data) {
      state.bag_logos = data;
    },
    setPelletDiameters(state, data) {
      state.pellet_diameters = data;
    },
    setUnitWeights(state, data) {
      state.unit_weights = data;
    },
    setTotalWeights(state, data) {
      state.total_weights = data;
    },
    setSalesOrders(state, payload) {
      state.salesOrders = payload;
    },
    setTextNotes(state, payload) {
      state.text_notes = payload;
    },
    setCorrectionReasons(state, payload) {
      state.correction_reasons = payload;
    },
    setBaseCurrency(state, currency) {
      state.base_currency = currency;
    }
  },
  actions: {
    async getWood(store) {
      try {
        const data = await Vue.http.get('woodware/list/trees/').then(handlerResponse);
        store.commit('setWood', data);
      } catch (exception) {
        console.log(exception);
      }
    },
    getIncoterms(store) {
      Vue.http.get('woodware/list/incoterms/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getIncoterms', data);
        });
    },
    async getDeliveryMethods(store) {
      try {
        const data = await Vue.http.get('woodware/list/delivery_methods/').then(handlerResponse);
        store.commit('getDeliveryMethods', data);
      } catch (exception) {
        console.log(exception);
      }
    },
    async getQualitiesList(store) {
      try {
        const data = await Vue.http.get('woodware/list/qualities/').then(handlerResponse);
        store.commit('setQualitiesList', data);
      } catch (exception) {
        console.log(exception);
      }
    },
    getUsers(store) {
      Vue.http.get('woodware/list/users/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getUsers', data);
        });
    },
    getDeliveryAddresses(store, payLoad) {
      Vue.http.get('woodware/list/delivery_addresses/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('getDeliveryAddresses', data);
        });
    },
    getPaymentTerms(store) {
      Vue.http.get('woodware/list/payment_terms/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getPaymentTerms', data);
        });
    },
    async getPartners(store, params) {
      try {
        const data = await Vue.http.get('woodware/list/partners/', {params}).then(handlerResponse);
        store.commit('setPartners', data);
      } catch (e) {
        console.log(e);
      }
    },
    getCountries(store) {
      Vue.http.get('woodware/list/countries/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getCountries', data);
        });
    },
    async getProfiles(store) {
      try {
        const data = await Vue.http.get('woodware/list/profiles/').then(handlerResponse);
        store.commit('setProfiles', data);
      } catch (e) {
        console.log(e)
      }
    },
    async getStockAddresses(store) {
      try {
        const data = await Vue.http.get('woodware/list/stock_addresses/').then(handlerResponse);
        store.commit('getStockAddresses', data);
        return data;
      } catch (e) {
        console.log(e);
      }
    },
    getPeriods(store) {
      const params = {for_vue: true};
      Vue.http.get('woodware/list/movement_data_report_period/', {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('getPeriods', data);
        });
    },
    getProcesses(store) {
      Vue.http.get('woodware/list/processes/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getProcesses', data);
        });
    },
    getProductionPlaces(store) {
      Vue.http.get('woodware/list/wobjects/')
        .then(handlerResponse)
        .then(data => {
          store.commit('getProductionPlaces', data);
        });
    },
    getInvoiceAdditionalInfoList(store) {
      const params = {type__in: 'vat,certificate,other'}
      Vue.http.get('woodware/list/sales_invoice_additional_information/', {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('getInvoiceAdditionalInfo', data);
        });
    },
    async getGroupsWithHierarchy(store) {
      try {
        const data = await Vue.http.get('woodware/list/groups_with_hierarchy/').then(handlerResponse);
        store.commit('setGroupsWithHierarchy', data);
        store.commit('setGroups', data);
      } catch (e) {
        console.log(e)
      }
    },
    async getImprs(store) {
      try {
        const data = await Vue.http.get('woodware/list/imprs/').then(handlerResponse);
        store.commit('setImprs', data);
      } catch (e) {
        console.log(e);
      }
    },
    getImprSynonyms(store) {
      Vue.http.get('woodware/list/impr_synonyms/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setImprSynonyms', data);
        });
    },
    async getWoodCertificates(store) {
      try {
        const data = await Vue.http.get('woodware/list/wood_certificates/').then(handlerResponse);
        store.commit('setWoodCertificates', data);
      } catch (e) {
        console.log(e)
      }
    },
    getPackagesSizes(store, payLoad) {
      const params = {sizes: payLoad};
      Vue.http.get('woodware/list/packages_sizes_list/', {params})
        .then(handlerResponse)
        .then(data => {
          switch (payLoad) {
            case 'length':
              store.commit('setPackagesLengths', data[payLoad]);
              break;
            case 'width':
              store.commit('setPackagesWidths', data[payLoad]);
              break;
            default :
              store.commit('setPackagesThicknesses', data[payLoad]);
          }
        });
    },
    getPiecesList(store) {
      Vue.http.get('woodware/list/packages_pieces_list/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setPiecesList', data);
        });
    },
    async loadHumidityList(store) {
      try {
        const data = await Vue.http.get('woodware/list/tree_humidity_list/').then(handlerResponse);
        store.commit('setHumidityList', data);
      } catch (e) {
        console.log(e)
      }
    },
    loadManufactureExports(store) {
      Vue.http.get('woodware/report/reports_views_list/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setManufactureExports', data);
        });
    },
    loadPacketsReports(store) {
      Vue.http.get('woodware/movement_data_report/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setPacketsReports', data);
        });
    },
    loadProcessPrices(store, params) {
      Vue.http.get('woodware/prices/process_prices/', {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setProcessPrices', data);
        });
    },
    loadFixedPrices(store, params) {
      Vue.http.get('woodware/prices/fixed_prices/', {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setFixedPrices', data);
        });
    },
    loadPackagesScores(store, params) {
      Vue.http.get('woodware/prices/packages_scores/', {params})
        .then(handlerResponse)
        .then(data => {
          store.commit('setPackagesScores', data);
        });
    },
    loadShipmentDriverNames(store) {
      Vue.http.get('woodware/list/shipment_driver_names/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setShipmentDriverNames', data);
        });
    },
    loadProductTemplates(store) {
      Vue.http.get('woodware/product_templates/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setProductTemplates', data);
        });
    },
    loadMovementGroups(store) {
      Vue.http.get('woodware/list/groups_with_hierarchy/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setMovementGroups', data);
        });
    },
    async fetchTemplates(store, type = 'operation') {
      try {
        const data = await Vue.http.get(`woodware/templates/?type=${type}`).then(handlerResponse);
        store.commit('setTemplates', data);
      } catch (e) {
        console.log(e)
      }
    },
    async fetchOperations(store) {
      try {
        const data = await Vue.http.get(`woodware/list/active_operations/`).then(handlerResponse);
        store.commit('setOperations', data);
      } catch (e) {
        console.log(e)
      }
    },
    loadGeneralDocuments(store) {
      Vue.http.get('woodware/templates/')
        .then(handlerResponse)
        .then(data => {
          store.commit('setGeneralDocuments', data);
        })
    },
    loadShortInvoiceTypesList(store, payLoad) {
      Vue.http.get('woodware/list/invoice_types/', {params: payLoad})
        .then(handlerResponse)
        .then(data => {
          store.commit('setShortInvoiceTypesList', data);
        });
    },
    loadSalesOrders({commit}) {
      return new Promise(resolve => {
        Vue.http.get(`woodware/list/salesorders/`, { params: { status: 'forecast,final' } })
          .then(handlerResponse)
          .then(data => {
            commit('setSalesOrders', data);
            resolve(data);
          });
      });
    },
    loadTextNotes({commit}) {
      return new Promise(resolve => {
        Vue.http.get(`woodware/list/sales_invoice_additional_information/?type__in=vat,certificate,other`)
          .then(handlerResponse)
          .then(data => {
            commit('setTextNotes', data);
            resolve(data);
          });
      });
    },
    loadCorrectionReasons({commit}) {
      return new Promise(resolve => {
        Vue.http.get(`woodware/list/sales_invoice_additional_information/?type__in=correction_reason`)
          .then(handlerResponse)
          .then(data => {
            commit('setCorrectionReasons', data);
            resolve(data);
          });
      });
    },
    async getBaseCurrency({commit}) {
      try {
        const data = await Vue.http.get('woodware/list/base_currency_data/').then(handlerResponse);
        commit('setBaseCurrency', data);
      } catch (error) {
        console.error(error.data);
      }
    },
    async fetchPalletSizes(store) {
      try {
        const data = await Vue.http.get('woodware/list/pallet_sizes/').then(handlerResponse);
        store.commit('setPalletSizes', data);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchBagLogos(store) {
      try {
        const data = await Vue.http.get('woodware/list/bag_logos/').then(handlerResponse);
        store.commit('setBagLogos', data);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchPelletDiameters(store) {
      try {
        const data = await Vue.http.get('woodware/list/pellet_diameters/').then(handlerResponse);
        store.commit('setPelletDiameters', data);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchTotalWeights(store) {
      try {
        const data = await Vue.http.get('woodware/list/packages_sizes_list/?sizes=weight').then(handlerResponse);
        store.commit('setTotalWeights', data.weight);
      } catch (e) {
        console.error(e);
      }
    },
    async fetchUnitWidths(store) {
      try {
        const data = await Vue.http.get('woodware/list/packages_sizes_list/?sizes=unit_weight').then(handlerResponse);
        store.commit('setUnitWeights', data.unit_weight);
      } catch (e) {
        console.error(e);
      }
    },
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/woodware.js